import { useLayoutEffect, useRef } from 'react';
import * as THREE from 'three';
// Relative imports (not `@/`) and no hexmap index so bun can test this module.
import config from '@/game/hexmap/config';
import { axialToWorld } from '@/game/hexmap/coords';
import type { HexTileMapKey } from '@/game/hexmap/model/types';

const RADIUS = config.hexMap.tile.radius * 0.96;
// Flat hex; thetaStart π/2 puts a vertex on ±Z to match the pointy-top tiles.
const hexGeometry = new THREE.CircleGeometry(RADIUS, 6, Math.PI / 2);
hexGeometry.rotateX(-Math.PI / 2);
// A bright rim on each tile keeps the tint readable when zoomed far out.
const rimGeometry = new THREE.RingGeometry(RADIUS * 0.8, RADIUS, 6, 1, Math.PI / 2);
rimGeometry.rotateX(-Math.PI / 2);
// Mirror the --color-ok / --color-accent tokens in index.css (three can't read CSS tokens).
const TONES = { ok: '#6ee7b7', accent: '#7dd3fc' } as const;
type Tone = keyof typeof TONES;
const material = (color: string, opacity: number) =>
  new THREE.MeshBasicMaterial({ color, transparent: true, opacity, depthWrite: false });
const fillMaterials: Record<Tone, THREE.Material> = {
  ok: material(TONES.ok, 0.35),
  accent: material(TONES.accent, 0.35),
};
const rimMaterials: Record<Tone, THREE.Material> = {
  ok: material(TONES.ok, 0.85),
  accent: material(TONES.accent, 0.85),
};
const noRaycast = () => null;
const dummy = new THREE.Object3D();

/**
 * Instanced tint over every tile where the armed building can be placed (or,
 * with `tone="accent"`, where the selected settler could found a city).
 */
export function PlacementOverlay({
  tiles,
  tone = 'ok',
}: {
  tiles: ReadonlySet<HexTileMapKey>;
  tone?: Tone;
}) {
  const fillRef = useRef<THREE.InstancedMesh>(null);
  const rimRef = useRef<THREE.InstancedMesh>(null);
  const capacity = Math.max(1, tiles.size);
  // The tiles written last: a new set with the same keys (recomputed each tick) skips the rewrite.
  const writtenRef = useRef<ReadonlySet<HexTileMapKey> | null>(null);

  useLayoutEffect(() => {
    const fill = fillRef.current;
    const rim = rimRef.current;
    if (!fill || !rim) return;
    const written = writtenRef.current;
    if (written && written.size === tiles.size && [...tiles].every((k) => written.has(k))) return;
    writtenRef.current = tiles;
    let i = 0;
    for (const key of tiles) {
      const [q, r] = key.split(',').map(Number);
      const world = axialToWorld({ q, r }, config.hexMap.tile.radius);
      dummy.position.set(world.x, config.hexMap.tile.thickness + 0.01, world.z);
      dummy.updateMatrix();
      fill.setMatrixAt(i, dummy.matrix);
      rim.setMatrixAt(i, dummy.matrix);
      i++;
    }
    fill.count = i;
    rim.count = i;
    fill.instanceMatrix.needsUpdate = true;
    rim.instanceMatrix.needsUpdate = true;
  }, [tiles]);

  // Remount when the capacity changes; instanced buffers are fixed-size.
  return (
    <group key={capacity}>
      <instancedMesh
        ref={fillRef}
        args={[hexGeometry, fillMaterials[tone], capacity]}
        raycast={noRaycast}
        frustumCulled={false}
      />
      <instancedMesh
        ref={rimRef}
        args={[rimGeometry, rimMaterials[tone], capacity]}
        raycast={noRaycast}
        frustumCulled={false}
      />
    </group>
  );
}
